import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { oneKlicks } from '../SDK/oneclick';
import { toast } from 'react-toastify';
import { Badge, Spinner } from 'react-bootstrap';

const UserChat = ({ doctorId, doctorName, appointmentId, onBack, onClose }) => {
    const { userData } = useAuth();
    const navigate = useNavigate();
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [isConnected, setIsConnected] = useState(false);
    const [doctorInfo, setDoctorInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const messagesEndRef = useRef(null);
    const wsRef = useRef(null);

    // Call states
    const [isInCall, setIsInCall] = useState(false);
    const [callType, setCallType] = useState(null); // 'voice' or 'video'
    const [localStream, setLocalStream] = useState(null);
    const [remoteStream, setRemoteStream] = useState(null);
    const [isCallActive, setIsCallActive] = useState(false);
    const [isCallConnecting, setIsCallConnecting] = useState(false);
    const [callDuration, setCallDuration] = useState(0);
    const [isMuted, setIsMuted] = useState(false);
    const [isVideoOff, setIsVideoOff] = useState(false);
    const [isRinging, setIsRinging] = useState(false);

    const displayDoctorName = useMemo(() => {
        if (!doctorName) return 'Doctor';
        const cleaned = doctorName.trim().replace(/^(Dr\.?\s*)+/i, '').trim();
        return cleaned.length ? cleaned : doctorName.trim();
    }, [doctorName]);

    // Refs for video elements
    const localVideoRef = useRef(null);
    const remoteVideoRef = useRef(null);
    const peerConnectionRef = useRef(null);
    const callTimerRef = useRef(null);

    useEffect(() => {
        fetchDoctorInfo();
        fetchChatHistory();
        initializeWebSocket();

        return () => {
            if (wsRef.current) wsRef.current.close();
            endCall();
        };
    }, [doctorId]);

    useEffect(() => {
        if (doctorId && userData?.id) fetchChatHistory();
    }, [doctorId, userData?.id]);

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    useEffect(() => {
        if (isCallActive) {
            callTimerRef.current = setInterval(() => setCallDuration(prev => prev + 1), 1000);
        } else {
            if (callTimerRef.current) {
                clearInterval(callTimerRef.current);
                callTimerRef.current = null;
            }
        }
        return () => clearInterval(callTimerRef.current);
    }, [isCallActive]);

    const fetchDoctorInfo = async () => {
        try {
            const response = await oneKlicks.read('', 'users', { id: doctorId }, '', '', '', '');
            if (response.success && response.data.length > 0) setDoctorInfo(response.data[0]);
        } catch (error) {
            console.error('Error fetching doctor info:', error);
        }
    };

    const fetchChatHistory = async () => {
        try {
            const tableSchema = '';
            const tableName = 'chat_messages';

            const [u2d, d2u] = await Promise.all([
                oneKlicks.read(tableSchema, tableName, { sender_id: userData?.id, receiver_id: doctorId }, 'created_at ASC', '', '', ''),
                oneKlicks.read(tableSchema, tableName, { sender_id: doctorId, receiver_id: userData?.id }, 'created_at ASC', '', '', '')
            ]);

            let allMessages = [];
            if (u2d.success && u2d.data) allMessages = [...allMessages, ...u2d.data];
            if (d2u.success && d2u.data) allMessages = [...allMessages, ...d2u.data];

            allMessages.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
            setMessages(allMessages);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching chat history:', error);
            setLoading(false);
        }
    };

    const initializeWebSocket = () => {
        try {
            const mockWebSocket = {
                send: (message) => {
                    const messageData = JSON.parse(message);
                    const newMsg = {
                        id: Date.now(),
                        sender_id: userData?.id,
                        sender_type: 'user',
                        receiver_id: doctorId,
                        receiver_type: 'doctor',
                        message: messageData.message,
                        created_at: new Date().toISOString().slice(0, 19).replace('T', ' '),
                        appointment_id: appointmentId || null
                    };
                    setMessages(prev => [...prev, newMsg]);
                    saveMessageToDatabase(newMsg);

                    setTimeout(() => {
                        const doctorResponse = {
                            id: Date.now() + 1,
                            sender_id: doctorId,
                            sender_type: 'doctor',
                            receiver_id: userData?.id,
                            receiver_type: 'user',
                            message: `Thank you for your message. Dr. ${displayDoctorName} will respond shortly.`,
                            created_at: new Date().toISOString().slice(0, 19).replace('T', ' '),
                            appointment_id: appointmentId || null
                        };
                        setMessages(prev => [...prev, doctorResponse]);
                        saveMessageToDatabase(doctorResponse);
                    }, 2000);
                },
                close: () => setIsConnected(false)
            };
            wsRef.current = mockWebSocket;
            setIsConnected(true);
        } catch (error) {
            console.error('WebSocket connection error:', error);
            toast.error('Failed to connect to chat server');
        }
    };

    const saveMessageToDatabase = async (message) => {
        try {
            const data = {
                sender_id: message.sender_id,
                sender_type: message.sender_type,
                receiver_id: message.receiver_id,
                receiver_type: message.receiver_type,
                message: message.message,
                appointment_id: message.appointment_id,
                created_at: message.created_at
            };
            await oneKlicks.create('', 'chat_messages', data);
        } catch (error) {
            console.error('Error saving message:', error);
        }
    };

    const sendMessage = (e) => {
        e.preventDefault();
        if (!newMessage.trim() || !isConnected) return;
        wsRef.current.send(JSON.stringify({ message: newMessage.trim(), timestamp: new Date().toISOString() }));
        setNewMessage('');
    };

    const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });

    const handleVoiceCall = async () => {
        if (!isConnected) return toast.error('Wait for connection');
        setIsCallConnecting(true);
        setIsRinging(true);
        setCallType('voice');
        setIsInCall(true);
        setTimeout(() => {
            setIsCallActive(true);
            setIsCallConnecting(false);
            setIsRinging(false);
        }, 3000);
    };

    const handleVideoCall = async () => {
        if (!isConnected) return toast.error('Wait for connection');
        setIsCallConnecting(true);
        setIsRinging(true);
        setCallType('video');
        setIsInCall(true);
        setTimeout(() => {
            setIsCallActive(true);
            setIsCallConnecting(false);
            setIsRinging(false);
        }, 3000);
    };

    const endCall = () => {
        setIsInCall(false);
        setIsCallActive(false);
        setIsCallConnecting(false);
        setIsRinging(false);
        setCallType(null);
        setCallDuration(0);
        setIsMuted(false);
        setIsVideoOff(false);
        if (localStream) localStream.getTracks().forEach(track => track.stop());
        if (remoteStream) remoteStream.getTracks().forEach(track => track.stop());
        setLocalStream(null);
        setRemoteStream(null);
    };

    const toggleMute = () => setIsMuted(!isMuted);
    const toggleVideo = () => setIsVideoOff(!isVideoOff);

    const formatCallDuration = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    const formatTime = (dateString) => {
        try {
            return new Date(dateString).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
        } catch (error) { return ''; }
    };

    const formatDate = (dateString) => {
        try {
            const date = new Date(dateString);
            const today = new Date().toDateString();
            if (date.toDateString() === today) return 'Today';
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        } catch (error) { return ''; }
    };

    const handleBackClick = () => {
        if (onBack) {
            onBack();
        } else if (onClose) {
            onClose();
        } else {
            navigate('/user-appointments');
        }
    };

    const groupMessagesByDate = (messages) => {
        const groups = {};
        messages.forEach(m => {
            const d = formatDate(m.created_at);
            if (!groups[d]) groups[d] = [];
            groups[d].push(m);
        });
        return groups;
    };

    const groupedMessages = groupMessagesByDate(messages);

    return (
        <div style={{ width: '100%', height: '100%', backgroundColor: '#f9fafb', display: 'flex', flexDirection: 'column', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
            {/* Premium Header */}
            <div className="chat-header px-4 py-3 bg-white d-flex align-items-center justify-content-between border-bottom" style={{ height: '80px' }}>
                <div className="d-flex align-items-center gap-3">
                    <button onClick={handleBackClick} className="btn btn-link p-0 text-muted me-2">
                        <i className="ti-arrow-left" style={{ fontSize: '20px' }}></i>
                    </button>
                    <div className="position-relative">
                        {doctorInfo?.profile_image ? (
                            <img src={doctorInfo.profile_image} alt="" style={{ width: '48px', height: '48px', borderRadius: '12px', objectFit: 'cover' }} />
                        ) : (
                            <div style={{ width: '48px', height: '48px', borderRadius: '12px', backgroundColor: '#eff6ff', color: '#1e40af', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '18px' }}>
                                {displayDoctorName.charAt(0)}
                            </div>
                        )}
                        <span className="position-absolute bottom-0 end-0 border border-white rounded-circle" style={{ width: '12px', height: '12px', backgroundColor: isConnected ? '#10b981' : '#9ca3af' }}></span>
                    </div>
                    <div>
                        <div className="d-flex align-items-center gap-2">
                            <h6 className="mb-0 fw-bold text-dark">Dr. {displayDoctorName}</h6>
                            <Badge bg="success" className="bg-opacity-10 text-success" style={{ fontSize: '10px', fontWeight: '600' }}>Live Chat</Badge>
                        </div>
                        <small className="text-muted" style={{ fontSize: '12px' }}>{doctorInfo?.specialization || 'Consultant'}</small>
                    </div>
                </div>

                <div className="d-flex align-items-center gap-2">
                    <button onClick={handleVoiceCall} className="btn btn-light-primary rounded-pill d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px', backgroundColor: '#f3f4f6' }}>
                        <i className="ti-headphone-alt" style={{ color: '#4b5563' }}></i>
                    </button>
                    <button onClick={handleVideoCall} className="btn text-white rounded-pill d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px', backgroundColor: 'var(--themecolor)' }}>
                        <i className="ti-video-camera"></i>
                    </button>
                </div>
            </div>

            {/* Messages Area */}
            <div className="flex-grow-1 p-4 overflow-auto d-flex flex-column gap-4" style={{ backgroundColor: '#f9fafb', backgroundImage: 'radial-gradient(#e5e7eb 0.5px, transparent 0.5px)', backgroundSize: '20px 20px' }}>
                {loading ? (
                    <div className="text-center py-5">
                        <Spinner animation="border" size="sm" variant="primary" />
                        <p className="mt-2 text-muted small">Loading conversation...</p>
                    </div>
                ) : (
                    Object.entries(groupedMessages).map(([date, msgs]) => (
                        <div key={date} className="d-flex flex-column gap-3">
                            <div className="text-center my-2">
                                <span className="px-3 py-1 bg-white border rounded-pill text-muted shadow-sm" style={{ fontSize: '11px', fontWeight: '600' }}>{date}</span>
                            </div>
                            {msgs.map((m) => {
                                const isMe = m.sender_type === 'user';
                                return (
                                    <div key={m.id} className={`d-flex ${isMe ? 'justify-content-end' : 'justify-content-start'}`}>
                                        <div className={`p-3 position-relative ${isMe ? 'bg-primary text-white shadow-sm' : 'bg-white text-dark shadow-sm border'}`}
                                            style={{ maxWidth: '75%', borderRadius: isMe ? '18px 18px 4px 18px' : '18px 18px 18px 4px', fontSize: '14px', backgroundColor: isMe ? 'var(--themecolor)' : '#fff' }}>
                                            <div>{m.message}</div>
                                            <div className={`mt-1 text-end ${isMe ? 'text-white-50' : 'text-muted'}`} style={{ fontSize: '10px' }}>{formatTime(m.created_at)}</div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ))
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* Call Overlay */}
            {isInCall && (
                <div className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center bg-dark bg-opacity-95" style={{ zIndex: 10, backdropFilter: 'blur(10px)' }}>
                    <div className="text-center mb-5">
                        <div className="mb-4 position-relative">
                            <div className="rounded-circle bg-primary bg-opacity-20 position-absolute" style={{ width: '140px', height: '140px', top: '-10px', left: '-10px', animation: 'pulse 2s infinite' }}></div>
                            <img src={doctorInfo?.profile_image || ''} alt="" className="rounded-circle border border-4 border-primary" style={{ width: '120px', height: '120px', objectFit: 'cover' }} />
                        </div>
                        <h4 className="text-white fw-bold mb-1">Dr. {displayDoctorName}</h4>
                        <p className="text-white-50">{isRinging ? 'Ringing...' : isCallActive ? formatCallDuration(callDuration) : 'Connecting...'}</p>
                    </div>

                    <div className="d-flex gap-4">
                        <button onClick={toggleMute} className={`btn btn-lg rounded-circle ${isMuted ? 'btn-danger' : 'btn-light'}`} style={{ width: '60px', height: '60px' }}>
                            <i className={`ti-microphone${isMuted ? '-off' : ''}`}></i>
                        </button>
                        <button onClick={endCall} className="btn btn-lg btn-danger rounded-circle" style={{ width: '60px', height: '60px' }}>
                            <i className="ti-close"></i>
                        </button>
                        {callType === 'video' && (
                            <button onClick={toggleVideo} className={`btn btn-lg rounded-circle ${isVideoOff ? 'btn-danger' : 'btn-light'}`} style={{ width: '60px', height: '60px' }}>
                                <i className={`ti-video-camera${isVideoOff ? '-off' : ''}`}></i>
                            </button>
                        )}
                    </div>

                    <style>{`
                        @keyframes pulse {
                            0% { transform: scale(1); opacity: 0.8; }
                            50% { transform: scale(1.3); opacity: 0; }
                            100% { transform: scale(1); opacity: 0.8; }
                        }
                    `}</style>
                </div>
            )}

            {/* Input Bar */}
            <form onSubmit={sendMessage} className="p-3 bg-white border-top">
                <div className="d-flex align-items-center gap-2 p-1 bg-light rounded-pill border shadow-inner">
                    <button type="button" className="btn btn-link text-muted p-0 ms-2">
                        <i className="ti-face-smile" style={{ fontSize: '20px' }}></i>
                    </button>
                    <input
                        type="text"
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Type a message..."
                        className="form-control border-0 bg-transparent py-2 shadow-none"
                        style={{ fontSize: '14px' }}
                    />
                    <button type="submit" disabled={!newMessage.trim()} className="btn btn-primary rounded-circle d-flex align-items-center justify-content-center me-1"
                        style={{ width: '36px', height: '36px', backgroundColor: 'var(--themecolor)', borderColor: 'var(--themecolor)' }}>
                        <i className="ti-arrow-right"></i>
                    </button>
                </div>
            </form>
        </div>
    );
};

export default UserChat;
